import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Upload,
  FileText,
  Image as ImageIcon,
  File,
  Loader2,
  Calendar,
  MapPin,
  User,
  X,
  Pencil,
  Trash2,
  Plus,
  FileSpreadsheet,
} from 'lucide-react';
import type { Document, DocumentType, PersonWithCounts } from '@antree/shared';
import { DOCUMENT_TYPE_LABELS } from '@antree/shared';

interface DocumentsResponse {
  documents: Document[];
}

interface DocumentResponse {
  document: Document;
}

interface PersonsResponse {
  persons: PersonWithCounts[];
}

interface TagResponse {
  tag: {
    id: string;
    personId: string;
    person: { id: string; firstName: string | null; middleName: string | null; lastName: string | null } | null;
    notes: string | null;
  };
}

// Get icon for file type
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon className="h-8 w-8 text-blue-500" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText className="h-8 w-8 text-red-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) {
    return <FileSpreadsheet className="h-8 w-8 text-green-500" />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <FileText className="h-8 w-8 text-blue-600" />;
  }
  return <File className="h-8 w-8 text-gray-500" />;
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Format date for display
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  } catch {
    return dateStr;
  }
}

export function ProjectDocuments() {
  const { id: projectId } = useParams<{ id: string }>();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [persons, setPersons] = useState<PersonWithCounts[]>([]);
  const [loading, setLoading] = useState(true);

  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Edit state
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editType, setEditType] = useState<DocumentType>('OTHER');
  const [editDate, setEditDate] = useState('');

  // Tag state
  const [taggingDoc, setTaggingDoc] = useState<Document | null>(null);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [removingTagId, setRemovingTagId] = useState<string | null>(null);

  // Delete state
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      loadDocuments();
      loadPersons();
    }
  }, [projectId]);

  async function loadDocuments() {
    setLoading(true);
    const response = await api.get<DocumentsResponse>(`/projects/${projectId}/documents`);
    if (response.success && response.data) {
      setDocuments(response.data.documents);
    }
    setLoading(false);
  }

  async function loadPersons() {
    const response = await api.get<PersonsResponse>(`/projects/${projectId}/persons`);
    if (response.success && response.data) {
      setPersons(response.data.persons);
    }
  }

  // Upload handlers
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await uploadFiles(files);
    }
  }, [projectId]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await uploadFiles(files);
    }
    e.target.value = '';
  }, [projectId]);

  async function uploadFiles(files: File[]) {
    setUploading(true);
    setUploadError(null);

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);

      try {
        const response = await fetch(`/api/projects/${projectId}/documents`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error?.message || 'Upload fehlgeschlagen');
        }

        const data = await response.json();
        if (data.success && data.data?.document) {
          setDocuments(prev => [data.data.document, ...prev]);
        }
      } catch (error) {
        setUploadError(error instanceof Error ? error.message : 'Upload fehlgeschlagen');
      }
    }

    setUploading(false);
    setUploadDialogOpen(false);
  }

  // Edit handlers
  function openEditDialog(doc: Document) {
    setEditingDoc(doc);
    setEditTitle(doc.title);
    setEditDescription(doc.description || '');
    setEditType(doc.documentType);
    setEditDate(doc.documentDate || '');
    setEditError(null);
    setEditDialogOpen(true);
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingDoc) return;

    setSaving(true);
    setEditError(null);

    const response = await api.put<DocumentResponse>(`/projects/${projectId}/documents/${editingDoc.id}`, {
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      documentType: editType,
      documentDate: editDate || null,
    });

    if (response.success && response.data) {
      setDocuments(prev =>
        prev.map(d => d.id === editingDoc.id ? response.data!.document : d)
      );
      setEditDialogOpen(false);
      setEditingDoc(null);
    } else {
      setEditError(response.error?.message || 'Fehler beim Speichern');
    }
    setSaving(false);
  }

  // Delete handler
  async function handleDelete(docId: string) {
    if (!confirm('Dokument wirklich löschen?')) return;

    setDeletingDocId(docId);
    const response = await api.delete(`/projects/${projectId}/documents/${docId}`);
    if (response.success) {
      setDocuments(prev => prev.filter(d => d.id !== docId));
    }
    setDeletingDocId(null);
  }

  // Tag handlers
  function openTagDialog(doc: Document) {
    setTaggingDoc(doc);
    setSelectedPersonId('');
    setTagDialogOpen(true);
  }

  async function handleAddTag() {
    if (!taggingDoc || !selectedPersonId) return;

    setAddingTag(true);
    const response = await api.post<TagResponse>(`/projects/${projectId}/documents/${taggingDoc.id}/tags`, {
      personId: selectedPersonId,
    });

    if (response.success && response.data) {
      setDocuments(prev =>
        prev.map(d => {
          if (d.id === taggingDoc.id) {
            return { ...d, tags: [...d.tags, response.data!.tag] };
          }
          return d;
        })
      );
      if (response.data) {
        setTaggingDoc(prev => prev ? {
          ...prev,
          tags: [...prev.tags, response.data!.tag]
        } : null);
      }
      setSelectedPersonId('');
    }
    setAddingTag(false);
  }

  async function handleRemoveTag(docId: string, tagId: string) {
    setRemovingTagId(tagId);
    const response = await api.delete(`/projects/${projectId}/documents/${docId}/tags/${tagId}`);
    if (response.success) {
      setDocuments(prev =>
        prev.map(d => {
          if (d.id === docId) {
            return { ...d, tags: d.tags.filter(t => t.id !== tagId) };
          }
          return d;
        })
      );
      if (taggingDoc?.id === docId) {
        setTaggingDoc(prev => prev ? {
          ...prev,
          tags: prev.tags.filter(t => t.id !== tagId)
        } : null);
      }
    }
    setRemovingTagId(null);
  }

  function formatPersonName(person: { firstName: string | null; middleName?: string | null; lastName: string | null } | null): string {
    if (!person) return 'Unbekannt';
    const parts = [];
    if (person.firstName) parts.push(person.firstName);
    if (person.middleName) parts.push(person.middleName);
    if (person.lastName) parts.push(person.lastName);
    return parts.length > 0 ? parts.join(' ') : 'Unbekannt';
  }

  // Get persons not already tagged
  const untaggedPersons = taggingDoc
    ? persons.filter(p => !taggingDoc.tags.some(t => t.personId === p.id))
    : [];

  return (
    <div className="container py-8">
      <div className="flex flex-col gap-6">
        {/* Back link */}
        <Link to={`/projects/${projectId}`}>
          <Button variant="ghost" className="px-0 hover:bg-transparent hover:text-bronze-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zum Projekt
          </Button>
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dokumente</h1>
            <p className="text-muted-foreground mt-1">
              {documents.length} {documents.length === 1 ? 'Dokument' : 'Dokumente'}
            </p>
          </div>
          <Button onClick={() => setUploadDialogOpen(true)}>
            <Upload className="mr-2 h-4 w-4" />
            Hochladen
          </Button>
        </div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dokument hochladen</DialogTitle>
              <DialogDescription>
                Ziehe Dateien hierher oder klicke zum Auswählen.
              </DialogDescription>
            </DialogHeader>
            <div
              className={`mt-4 border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p>Wird hochgeladen...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <p>Dateien hierher ziehen</p>
                  <p className="text-sm text-muted-foreground">oder</p>
                  <label>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                      onChange={handleFileSelect}
                    />
                    <Button variant="outline" asChild>
                      <span>Dateien auswählen</span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-2">
                    Bilder, PDFs, Word- und Excel-Dateien (max. 50 MB)
                  </p>
                </div>
              )}
            </div>
            {uploadError && (
              <p className="text-sm text-red-500 mt-2">{uploadError}</p>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dokument bearbeiten</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveEdit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="editTitle">Titel</Label>
                <Input
                  id="editTitle"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editDescription">Beschreibung</Label>
                <Textarea
                  id="editDescription"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="editType">Dokumenttyp</Label>
                  <Select value={editType} onValueChange={(v) => setEditType(v as DocumentType)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="editDate">Datum</Label>
                  <Input
                    id="editDate"
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                  />
                </div>
              </div>
              {editError && <p className="text-sm text-red-500">{editError}</p>}
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tag Dialog */}
        <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Personen verknüpfen</DialogTitle>
              <DialogDescription>
                {taggingDoc?.title}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Add person */}
              <div className="flex gap-2">
                <Select value={selectedPersonId} onValueChange={setSelectedPersonId}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Person auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {untaggedPersons.map((person) => (
                      <SelectItem key={person.id} value={person.id}>
                        {[person.firstName, person.middleName, person.lastName].filter(Boolean).join(' ') || 'Unbekannt'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddTag} disabled={!selectedPersonId || addingTag}>
                  {addingTag ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>

              {/* Tagged persons */}
              <div className="space-y-2">
                <Label>Verknüpfte Personen</Label>
                {taggingDoc?.tags.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Noch keine Personen verknüpft</p>
                ) : (
                  <div className="space-y-1">
                    {taggingDoc?.tags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between p-2 rounded border bg-muted/30"
                      >
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{formatPersonName(tag.person)}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => handleRemoveTag(taggingDoc!.id, tag.id)}
                          disabled={removingTagId === tag.id}
                        >
                          {removingTagId === tag.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <X className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Documents List */}
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : documents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium">Noch keine Dokumente</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Lade dein erstes Dokument hoch.
              </p>
              <Button className="mt-4" onClick={() => setUploadDialogOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Hochladen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {documents.map((doc) => (
              <Card key={doc.id} className="overflow-hidden">
                {/* Thumbnail or icon */}
                <div className="aspect-[4/3] bg-muted flex items-center justify-center relative group">
                  {doc.isImage ? (
                    <img
                      src={doc.fileUrl}
                      alt={doc.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getFileIcon(doc.mimeType)
                  )}
                  {/* Hover overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => openEditDialog(doc)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => openTagDialog(doc)}
                    >
                      <User className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="icon"
                      onClick={() => handleDelete(doc.id)}
                      disabled={deletingDocId === doc.id}
                    >
                      {deletingDocId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base truncate">{doc.title}</CardTitle>
                  <CardDescription>
                    {DOCUMENT_TYPE_LABELS[doc.documentType]} · {formatFileSize(doc.fileSize)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {doc.documentDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{formatDate(doc.documentDate)}</span>
                      </div>
                    )}
                    {doc.place && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="truncate">{doc.place.name}</span>
                      </div>
                    )}
                    {doc.tags.length > 0 && (
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5" />
                        <span className="truncate">
                          {doc.tags.map(t => formatPersonName(t.person)).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
